<script setup lang="ts">
import GwMenu from '@/components/lib/GwMenu.vue'
import GwBtns from '@/components/lib/GwBtns.vue'
import { min } from 'lodash'
import { computed } from 'vue'
import { numKtoM } from '@/helpers/formating'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import { t } from '@/locales/i18n'
import type {
  GwPaginationProps,
  GwPaginationEmitProps,
} from '@/components/lib/GwPagination/gwPaginationUtils'
import GwTooltipNew from '../GwTooltip/GwTooltipNew.vue'

const props = withDefaults(defineProps<GwPaginationProps>(), {
  count: 0,
  limit: 50,
  loading: false,
  offset: 0,
})

const emit = defineEmits(['update:pagination'])

const hasNext = computed(() =>
  props.count
    ? props.count / props.limit > (props.offset + props.limit) / props.limit
    : props.hasNext,
)

const limits =
  import.meta.env.VITE_ENV === 'development' ? [1, 10, 20, 50, 100, 200, 500] : [50, 100, 200, 500]

const maxCount = computed(() => numKtoM(min([props.count, props.offset + props.limit])!, 10_000))
const alignedCount = computed(() => numKtoM(props.count, 10_000))

const update = (newLimit: number, newOffset: number) => {
  if (props.loading) return
  const res: GwPaginationEmitProps = {
    limit: newLimit,
    offset: newOffset < 0 ? 0 : newOffset,
  }
  emit('update:pagination', res)
}
</script>

<template>
  <div class="tbfttr">
    <div
      v-if="count"
      class="weight_500 text-medium ml-auto clr-grey mr-1"
      data-tst="pagination_total"
    >
      {{ offset + 1 }} - {{ maxCount }} {{ $t('common.of') }} {{ alignedCount }}
    </div>

    <GwTooltipNew
      :closeOnContentHover="true"
      placement="top"
      :tooltip="t('tooltips.pagination.first_page')"
    >
      <template #opener="{ listeners }">
        <div
          :data-tst="`first_page${offset <= limit ? '_disabled' : ''}`"
          :class="{ 'no-events clr-grey': offset <= limit }"
          class="gw_btn gw_btn_text weight_500 text-medium ml-auto"
          v-on="listeners"
          @click="update(limit, 0)"
        >
          <i
            class="mdi-chevron-double-left mdi"
            style="font-size: 1.25em"
          />
        </div>
      </template>
    </GwTooltipNew>

    <GwTooltipNew
      :closeOnContentHover="true"
      placement="top"
      :tooltip="t('tooltips.pagination.previous_page')"
    >
      <template #opener="{ listeners }">
        <div
          :data-tst="`prev_page${offset === 0 ? '_disabled' : ''}`"
          :class="{ 'no-events clr-grey': offset === 0 }"
          class="gw_btn gw_btn_text weight_500 text-medium"
          v-on="listeners"
          @click="update(limit, offset - limit)"
        >
          <i
            class="mdi-chevron-left mdi"
            style="font-size: 1.25em"
          />
        </div>
      </template>
    </GwTooltipNew>

    <GwTooltipNew
      :closeOnContentHover="true"
      placement="top"
      :tooltip="t('tooltips.pagination.next_page')"
    >
      <template #opener="{ listeners }">
        <div
          :data-tst="`next_page${hasNext ? '' : '_disabled'}`"
          :class="{ 'no-events clr-grey': !hasNext }"
          class="gw_btn gw_btn_text weight_500 text-medium"
          v-on="listeners"
          @click="update(limit, offset + limit)"
        >
          <i
            class="mdi-chevron-right mdi"
            style="font-size: 1.25em"
          />
        </div>
      </template>
    </GwTooltipNew>

    <GwMenu
      ref="gwmenu"
      :isEager="true"
      :isInside="true"
    >
      <template #opener>
        <div
          data-tst="open_pagination_limit"
          class="gw_btn weight_500 text-medium ml-1"
        >
          {{ limit }}
        </div>
      </template>
      <template #default="{ methods }">
        <GwBtns
          :options="limits"
          :value="limit"
          class="gwbtns_vertical_white gw_menu"
          @click="
            (_e, item) => {
              methods.toggleOpen(false)
              update(item, 0)
            }
          "
        >
          <template #default="{ item }">
            <slot
              name="item_before"
              :item="item"
            >
            </slot>
            <slot :item="item">
              {{ item }}
            </slot>
          </template>
        </GwBtns>
      </template>
    </GwMenu>
  </div>
</template>

<style lang="scss" scoped>
.tbfttr {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--app-gap-1);
}
</style>
